<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="goBack">Users</v-btn>
                    <h1 class="text-left mt-2">
                        IP Restrictions
                        <span v-if="user" class="text-medium-emphasis text-h5 ml-2">
                            for {{ user.first_name }} {{ user.last_name }}
                            <span class="text-body-1">({{ user.email }})</span>
                        </span>
                    </h1>
                    <p class="text-medium-emphasis mt-1">
                        Empty list means this user has no IP restriction. Adding one or more entries
                        restricts the user to those IPs or CIDR ranges. Single IPs and CIDR are both
                        accepted.
                    </p>
                </div>
                <v-btn color="black" prepend-icon="mdi-plus" @click="openCreate">Add IP</v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>IP or CIDR</th>
                        <th>Description</th>
                        <th>Added</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in rows" v-if="rows.length" :key="row.id" class="text-left">
                        <td><code>{{ row.ip_address }}</code></td>
                        <td>{{ row.description || '-' }}</td>
                        <td>{{ formatDate(row.created_at) }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn class="text-warning" prepend-icon="mdi-pencil" size="x-small"
                                       @click="openEdit(row)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteRow(row)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="4">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else class="text-center" colspan="4">No IP restrictions for this user.</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
    </v-container>

    <UserIpDialog v-if="dialogSwitch" v-model="dialogSwitch" :user-id="userId"
                  :existing="editingRow" @saved="onSaved"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import UserIpDialog from "@/components/dialogs/userIps/Create.vue";

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();

const userId = route.params.userId;
const dialogSwitch = ref(false);
const editingRow = ref(null);
const loading = ref(true);
const rows = ref([]);
const user = ref(null);

onMounted(() => {
    if (!authStore.user || !authStore.user.super) {
        showSnackbar('Only super admin users can manage IP restrictions.', 'error');
        router.replace({name: 'users'});
        return;
    }
    fetchUser();
    fetchRows();
});

const fetchUser = () => {
    send('get', `users/${userId}`)
        .then((response) => {
            user.value = response.data.data;
        })
        .catch(() => {
            // Non fatal: heading just shows the user id then.
        });
};

const fetchRows = () => {
    loading.value = true;
    send('get', `admin/users/${userId}/allowed-ips`)
        .then((response) => {
            rows.value = response.data.data || [];
        })
        .catch(() => {
            showSnackbar('Could not load IP restrictions.', 'error');
        })
        .finally(() => {
            loading.value = false;
        });
};

const openCreate = () => {
    editingRow.value = null;
    dialogSwitch.value = true;
};

const openEdit = (row) => {
    editingRow.value = row;
    dialogSwitch.value = true;
};

const onSaved = (row) => {
    const existingIdx = rows.value.findIndex((r) => r.id === row.id);
    if (existingIdx >= 0) {
        rows.value[existingIdx] = row;
    } else {
        rows.value.unshift(row);
    }
};

const deleteRow = (row) => {
    if (!confirm(`Remove ${row.ip_address} from this user's allowlist?`)) {
        return;
    }
    send('delete', `admin/users/${userId}/allowed-ips/${row.id}`)
        .then((response) => {
            if (response.data.success) {
                rows.value = rows.value.filter((r) => r.id !== row.id);
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => {
            const msg = error?.response?.data?.message || 'Could not remove entry.';
            showSnackbar(msg, 'error');
        });
};

const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
};

const goBack = () => {
    router.push({name: 'users'});
};
</script>
